"use client";

import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Points, PointMaterial, Float, Sphere, MeshDistortMaterial } from '@react-three/drei';
import * as THREE from 'three';
import ConditionalCanvas from "./ConditionalCanvas";

function ParticleNetwork({ count = 1000 }) {
  const points = useRef<THREE.Points>(null!);
  
  const particles = useMemo(() => {
    const temp = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const x = (Math.random() - 0.5) * 15;
      const y = (Math.random() - 0.5) * 15;
      const z = (Math.random() - 0.5) * 15;
      temp.set([x, y, z], i * 3);
    }
    return temp;
  }, [count]);

  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    points.current.rotation.y = time * 0.05;
    points.current.rotation.x = time * 0.02;
  });

  return (
    <Points ref={points} positions={particles} stride={3} frustumCulled={false}>
      <PointMaterial
        transparent
        color="#00f3ff"
        size={0.05}
        sizeAttenuation={true}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </Points>
  );
}

function GlowingSphere() {
  const mesh = useRef<THREE.Mesh>(null!);
  
  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    mesh.current.rotation.y = time * 0.2;
  });

  return (
    <Float speed={2} rotationIntensity={1} floatIntensity={2}>
      <Sphere ref={mesh} args={[3, 64, 64]}>
        <MeshDistortMaterial
          color="#0066FF"
          speed={3}
          distort={0.4}
          radius={1}
          emissive="#00f3ff"
          emissiveIntensity={0.5}
          wireframe
          transparent
          opacity={0.3}
        />
      </Sphere>
    </Float>
  );
}

export default function EcosystemHeroScene() {
  return (
    <div className="absolute inset-0 z-0 bg-[#040d21]">
      <ConditionalCanvas camera={{ position: [0, 0, 10], fov: 45 }}>
        <ambientLight intensity={0.5} />
        <pointLight position={[10, 10, 10]} intensity={1} color="#00f3ff" />
        <pointLight position={[-10, -10, -10]} intensity={0.5} color="#FF7A00" />
        
        <ParticleNetwork count={2000} />
        <GlowingSphere />
        
        {/* Subtle background fog effect */}
        <fog attach="fog" args={['#040d21', 5, 20]} />
      </ConditionalCanvas>
      
      {/* Overlay gradient to ensure text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#040d21] via-transparent to-[#040d21]/40 pointer-events-none" />
    </div>
  );
}
